// framework/client/core/RuntimeProgressManager.js
import { ComponentBase } from "../core/ComponentBase.js";

export class RuntimeProgressManager extends ComponentBase {

    static styles = `
            /* Styles for the progress indicator container */
            .runtime-progress-container {
                position: relative;
                display: inline-block;
            }
    
            /* Styles for the progress indicator button */
            .progress-indicator-button {
                cursor: pointer;
                background-color: var(--bs-btn-bg);
                color: var(--bs-btn-color);
                border: 1px solid var(--bs-btn-border-color);
            }
    
            .progress-indicator-button:hover {
                background-color: var(--bs-btn-hover-bg);
                color: var(--bs-btn-hover-color);
            }
    
            /* Styles for the progress badge */
            .progress-badge {
                position: absolute;
                top: 0;
                right: 0;
                transform: translate(50%, -50%);
                background-color: var(--bs-danger); /* Use Bootstrap's danger color for the badge */
                color: var(--bs-danger-text-emphasis, #fff);
                border-radius: 50%;
                padding: 2px 6px;
                font-size: var(--ab-text-xs);
                line-height: 1;
            }
    
            /* Styles for the dropdown menu */
            .progress-dropdown-menu {
                min-width: 20rem; /* Adjust as needed */
                max-height: 300px; /* Limit height and add scrollbar */
                overflow-y: auto;
                background-color: var(--bs-dropdown-bg);
                color: var(--bs-dropdown-color);
                border: 1px solid var(--bs-dropdown-border-color);
                border-radius: var(--bs-dropdown-border-radius);
                box-shadow: var(--bs-dropdown-box-shadow);
                padding: 0.5rem;
            }
    
            /* Styles for individual progress items */
            .progress-item {
                padding: 0.5rem;
                border-bottom: 1px solid var(--bs-dropdown-divider-bg, #eee);
                transition: background-color 0.2s;
                border-radius: var(--ab-radius-xs);
                margin-bottom: 8px;
            }
    
            .progress-item:last-child {
                border-bottom: none;
            }
    
            .progress-item:hover {
                background-color: rgba(0, 0, 0, 0.03);
            }
    
            .progress-item-title {
                font-weight: bold;
                display: flex;
                justify-content: space-between;
                align-items: center;
                margin-bottom: 6px; /* Added from later definition - kept */
            }
    
            .progress-item-status {
                font-style: italic;
                color: var(--bs-tertiary-color, #6c757d); /* Use a muted color */
            }
    
            .progress-bar-container {
                height: 10px;
                background-color: #f0f0f0;
                border-radius: var(--ab-radius-sm);
                margin-top: 0.25rem;
            }
    
            .progress-bar {
                height: 100%;
                background-color: var(--bs-primary); /* Use Bootstrap's primary color */
                border-radius: var(--ab-radius-sm);
                width: 0%; /* Initial width */
                transition: width 0.3s ease;
            }
    
            .dropdown-toggle::after {
                display: none !important;
            }
    
            /* Log container styles */
            .log-container {
                margin-top: 0.5rem;
                max-height: 200px;
                overflow-y: auto;
                background-color: var(--bs-f8f9fa); /* Changed to var --bs-tertiary-bg and border from later definition for consistency and distinct look */
                border-radius: var(--ab-radius-xs);
                padding: 0.5rem;
                font-family: monospace;
                font-size: var(--ab-text-sm);
                display: none; /* Hidden by default */
                border: 1px solid var(--bs-border-color); /* Added border from later definition */
                background-color: var(--bs-tertiary-bg); /* Changed to tertiary background for distinct look */
            }
    
            .log-container.show {
                display: block;
            }
    
            .log-toggle {
                cursor: pointer;
                font-size: var(--ab-text-sm);
                color: var(--bs-primary);
                margin-left: 0.5rem;
                padding: 2px 4px; /* Added from later definition */
                border-radius: var(--ab-radius-xs); /* Added from later definition */
                background-color: var(--bs-tertiary-bg); /* Added background from later definition */
            }
    
            .log-toggle:hover {
                text-decoration: underline;
                background-color: var(--bs-border-color); /* Added background hover from later definition */
            }
    
            /* Modal styles */
            .task-modal .modal-body {
                max-height: 70vh;
                overflow-y: auto;
            }
    
            .log-entry {
                margin-bottom: 2px;
                word-break: break-word;
            }
    
            .log-entry.error {
                color: var(--bs-danger);
            }
    
            .log-entry.stdout {
                color: var(--bs-dark);
            }
    
    
            /* Styles for control buttons - moved up to be with title styles and removed duplicate*/
    
    
            /* Styles for the action buttons in the task item - combined with control buttons section and removed duplicate*/
            .progress-item button {
                padding: 0.2rem 0.5rem;
                font-size: var(--ab-text-xs);
                opacity: 0.85;
            }
    
            .progress-item button:hover {
                opacity: 1;
            }
    
            /* Clear All button styling */
            .dropdown-menu .btn-outline-secondary {
                border-color: var(--bs-border-color);
                color: var(--bs-body-color);
            }
    
            .dropdown-menu .btn-outline-secondary:hover {
                background-color: var(--bs-tertiary-bg);
                border-color: var(--bs-border-color);
            }
    
            /* Status indicator styles */
            .task-status {
                font-weight: 500;
            }
    
            .task-status.completed {
                color: var(--bs-success);
            }
    
            .task-status.failed, .task-status.cancelled {
                color: var(--bs-danger);
            }
    
            .task-status.running {
                color: var(--bs-primary);
            }
    
            /* Time information styles */
            .task-time-info {
                font-size: var(--ab-text-sm);
                color: var(--bs-tertiary-color);
                margin-top: 4px;
            }
    
            /* Animation for running tasks */
            @keyframes pulse {
                0% {
                    opacity: 0.6;
                }
                50% {
                    opacity: 1;
                }
                100% {
                    opacity: 0.6;
                }
            }
    
            .progress-item[data-status="running"] .progress-bar {
                animation: pulse 2s infinite;
            }
    
            /* Modal footer button spacing */
            .task-modal .modal-footer {
                gap: 0.5rem;
            }
    
            /* Styles for button group in dropdown header */
            .dropdown-header-actions {
                display: flex;
                justify-content: space-between;
                padding: 0.5rem 1rem;
                border-bottom: 1px solid var(--bs-border-color);
                margin-bottom: 0.5rem;
            }
    
    
        `;

    constructor(config) {
        super(config);
        this.tasks = new Map(); // instanceId -> { taskName, type, status, progress, logData, startTime, endTime, remoteRpcClass }
        this.localStorageKey = "runtimeProgressTasks";
        this.container = null;
        this.taskListElement = null;
        this.dropdownInstance = null;
        this.activeModals = new Map(); // Track active modals by instanceId
        this.applyStyles();
    }

    async initialize() {
        this.loadFromLocalStorage();
        // Refetch status for all running tasks
        for (const [instanceId, task] of this.tasks.entries()) {
            if (task.status === 'running' && task.remoteRpcClass) {
                this.refetchTaskStatus(instanceId);
            }
        }
        this.appClient.events.server.on("process-progress", this.handleServerEvent.bind(this));
        this.appClient.events.local.on("process-progress", this.handleLocalEvent.bind(this));

        this.appClient.events.local.on("create-runtime-task", this.handleCreateTask.bind(this));
        this.appClient.events.local.on("end-runtime-task", this.handleEndTask.bind(this));
        await super.initialize();
    }


    render() {
        if (!this.container) {
            this.container = document.createElement('div');
            this.container.classList.add('runtime-progress-container', 'dropdown');

            this.progressButton = document.createElement('button');
            this.progressButton.classList.add('btn', 'dropdown-toggle', 'progress-indicator-button');
            this.progressButton.type = 'button';
            this.progressButton.setAttribute('data-bs-toggle', 'dropdown');
            this.progressButton.setAttribute('aria-expanded', 'false');
            this.progressButton.innerHTML = '<i class="bi bi-bar-chart-line-fill"></i>'; // Bootstrap icon
            this.container.appendChild(this.progressButton);

            this.badge = document.createElement('span');
            this.badge.classList.add('progress-badge');
            this.badge.style.display = 'none'; // Initially hidden
            this.container.appendChild(this.badge);

            this.dropDownContainer = document.createElement('div');
            this.dropDownContainer.classList.add('dropdown-menu')
            this.container.appendChild(this.dropDownContainer);

            this.taskControlElement = document.createElement('div');
            this.taskControlElement.classList.add('dropdown-header-actions');
            this.dropDownContainer.appendChild(this.taskControlElement);

            
            // Create and append Clear All button (initially hidden)
            this.clearAllButton = document.createElement('button');
            this.clearAllButton.classList.add('btn', 'btn-sm', 'btn-outline-secondary', 'w-100', 'mb-2', 'clear-all-tasks-button');
            this.clearAllButton.textContent = 'Clear All Completed Tasks';
            this.clearAllButton.setAttribute('data-i18n', 'Clear All Completed Tasks');
            this.clearAllButton.addEventListener('click', () => {
                this.clearCompletedTasks();
            });
            this.clearAllButton.style.display = 'none'; // Initially hidden
            this.taskControlElement.appendChild(this.clearAllButton);


            // Create and append "No tasks" message (initially hidden)
            this.noTasksMessageElement = document.createElement('div');
            this.noTasksMessageElement.classList.add('p-2', 'text-muted', 'no-tasks-message');
            this.noTasksMessageElement.textContent = 'No active or recent tasks.';
            this.noTasksMessageElement.setAttribute("data-i18n", 'No active or recent tasks.');
            this.noTasksMessageElement.style.display = 'none'; // Initially hidden
            this.taskControlElement.appendChild(this.noTasksMessageElement);

            this.taskListElement = document.createElement('div');
            this.taskListElement.classList.add('progress-dropdown-menu');
            this.dropDownContainer.appendChild(this.taskListElement);

            // Initialize dropdown using Bootstrap
            this.dropdownInstance = new bootstrap.Dropdown(this.progressButton);
        }

        this.updateTaskList(); // Initial task list update
        return this.container;
    }

    updateClearAllButtonVisibility() {
        const hasCompletedTasks = Array.from(this.tasks.values()).some(task =>
            task.status === 'completed' || task.status === 'failed' || task.status === 'cancelled'
        );
        this.setClearAllButtonVisible(hasCompletedTasks);
    }

    updateNoTasksMessageVisibility() {
        const hasTasks = this.tasks.size > 0;
        this.setNoTasksMessageVisible(!hasTasks);
    }


    setClearAllButtonVisible(visible) {
        if (this.clearAllButton) {
            this.clearAllButton.style.display = visible ? '' : 'none';
        }
    }

    setNoTasksMessageVisible(visible) {
        if (this.noTasksMessageElement) {
            this.noTasksMessageElement.style.display = visible ? '' : 'none';
            this.taskListElement.style.display = visible ? 'none' : '';
        }
    }



    updateBadge() {
        const runningTasks = Array.from(this.tasks.values()).filter(task => task.status === 'running').length;
        this.badge.textContent = runningTasks;
        this.badge.style.display = runningTasks > 0 ? 'inline-block' : 'none';
    }

    updateTaskList() {
        this.taskListElement.innerHTML = ''; // Clear task items, but keep button and message

        this.updateClearAllButtonVisibility(); // Update Clear All button visibility
        this.updateNoTasksMessageVisibility(); // Update "No tasks" message visibility


        if (this.tasks.size > 0) {
            // Hide "No active or recent tasks" message if tasks are present
            this.setNoTasksMessageVisible(false);
            for (const [instanceId, task] of this.tasks.entries()) {
                const taskItem = this.createTaskElement(task);
                this.taskListElement.appendChild(taskItem);
            }
        } else {
            // Ensure "No active or recent tasks" message is shown if no tasks
            this.setNoTasksMessageVisible(true);
        }
        this.appClient.tx.applyTranslations(this.taskListElement);
    }


    createTaskElement(task) {
        const item = document.createElement('div');
        item.classList.add('progress-item');
        item.setAttribute('data-instance-id', task.instanceId);
        item.setAttribute('data-status', task.status);

        // Make the entire item clickable to show modal
        item.addEventListener('click', (e) => {
            // Don't trigger if clicking on buttons or log toggle
            if (!e.target.closest('.btn') && !e.target.closest('.log-toggle')) {
                this.showTaskModal(task);
            }
        });
        item.style.cursor = 'pointer';

        // Header with title and controls
        const headerDiv = document.createElement('div');
        headerDiv.classList.add('progress-item-title');

        const titleSpan = document.createElement('span');
        titleSpan.textContent = task.taskName;
        headerDiv.appendChild(titleSpan);

        const controlsDiv = document.createElement('div');
        controlsDiv.classList.add('d-flex', 'gap-2');

        const logToggle = document.createElement('button'); // Changed to button for consistent styling
        logToggle.classList.add('log-toggle', 'btn', 'btn-sm', 'btn-outline-secondary'); // Added btn classes for consistent styling
        logToggle.innerHTML = '<i class="bi bi-journal-text"></i>'; // Bootstrap icon for log
        logToggle.title = 'View Log'; // Title attribute for tooltip/accessibility
        logToggle.setAttribute('data-i18n-title', 'View Log'); // For i18n of title
        logToggle.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            const logContainer = item.querySelector('.log-container');
            const isVisible = logContainer.classList.toggle('show');
            logToggle.innerHTML = isVisible ? '<i class="bi bi-journal-text"></i>' : '<i class="bi bi-journal-text"></i>'; // Keep same icon, maybe change later if needed
            logToggle.title = isVisible ? 'Hide Log' : 'View Log';
            logToggle.setAttribute('data-i18n-title', isVisible ? 'Hide Log' : 'View Log');
            // Apply translations to the toggle button (including title)
            this.appClient.tx.applyTranslations(logToggle);
        });
        controlsDiv.appendChild(logToggle);

        // Add cancel button for running tasks that allow cancellation
        if (task.status === 'running' && task.allowCancel) {
            const cancelBtn = document.createElement('button');
            cancelBtn.classList.add('btn', 'btn-sm', 'btn-danger');
            cancelBtn.innerHTML = '<i class="bi bi-x-circle-fill"></i>'; // Bootstrap cancel icon
            cancelBtn.title = 'Cancel Task'; // Title attribute
            cancelBtn.setAttribute('data-i18n-title', 'Cancel Task'); // For i18n of title
            cancelBtn.addEventListener('click', (e) => {
                e.preventDefault();
                e.stopPropagation();
                this.cancelTask(task.instanceId);
            });
            controlsDiv.appendChild(cancelBtn);
        }

        // Add remove button for completed, failed, or cancelled tasks
        if (task.status === 'completed' || task.status === 'failed' || task.status === 'cancelled') {
            const removeBtn = document.createElement('button');
            removeBtn.classList.add('btn', 'btn-sm', 'btn-outline-secondary');
            removeBtn.innerHTML = '<i class="bi bi-trash-fill"></i>'; // Bootstrap remove/trash icon
            removeBtn.title = 'Remove Task'; // Title attribute
            removeBtn.setAttribute('data-i18n-title', 'Remove Task'); // For i18n of title
            removeBtn.addEventListener('click', (e) => {
                e.preventDefault();
                e.stopPropagation();
                this.removeTask(task.instanceId);
            });
            controlsDiv.appendChild(removeBtn);
        }

        headerDiv.appendChild(controlsDiv);
        item.appendChild(headerDiv);

        // Status
        const statusDiv = document.createElement('div');
        statusDiv.classList.add('progress-item-status', `task-status`, task.status);
        statusDiv.textContent = task.status;
        item.appendChild(statusDiv);

        // Progress message (if available)
        if (task.progressMessage) {
            const messageDiv = document.createElement('div');
            messageDiv.classList.add('progress-message');
            messageDiv.textContent = task.progressMessage;
            item.appendChild(messageDiv);
        }

        // Progress bar
        const progressBarContainer = document.createElement('div');
        progressBarContainer.classList.add('progress-bar-container');

        const progressBar = document.createElement('div');
        progressBar.classList.add('progress-bar');
        progressBar.style.width = `${task.progress}%`;

        progressBarContainer.appendChild(progressBar);
        item.appendChild(progressBarContainer);

        // Log container
        const logContainer = document.createElement('div');
        logContainer.classList.add('log-container');

        // Add existing log entries
        this.renderLogEntries(logContainer, task.logData);

        item.appendChild(logContainer);

        return item;
    }


    renderLogEntries(container, logData) {
        container.innerHTML = '';

        if (!logData || logData.length === 0) {
            const emptyLog = document.createElement('div');
            emptyLog.textContent = 'No log entries available';
            emptyLog.setAttribute('data-i18n', emptyLog.textContent);
            container.appendChild(emptyLog);
            return;
        }

        for (const entry of logData) {
            const logEntry = document.createElement('div');
            logEntry.classList.add('log-entry');

            if (typeof entry === 'string') {
                logEntry.textContent = entry;
            } else if (entry && typeof entry === 'object') {
                if (entry.type) {
                    logEntry.classList.add(entry.type);
                }
                logEntry.textContent = entry.data || JSON.stringify(entry);
            }

            container.appendChild(logEntry);
        }
    }

    handleCreateTask(eventData) {
        const { instanceId, taskName, type, remoteRpcClass, showPopup, allowCancel, progressMessage } = eventData;
        this.addTask(instanceId, taskName, type, remoteRpcClass, showPopup, allowCancel, progressMessage);
    }


    async cancelTask(instanceId) {
        const task = this.tasks.get(instanceId);
        if (!task || task.status !== 'running' || !task.remoteRpcClass) {
            return;
        }

        try {
            await this.appClient.rpcClient.call(`${task.remoteRpcClass}.stopProcess`, { instanceId });
            task.status = 'cancelled';
            task.endTime = Date.now();
            this.updateTaskDisplay(task);
            this.updateModalDisplay(task);
            this.saveToLocalStorage();
            this.updateBadge();
            this.appClient.events.fireLocal('runtime-task-ended', { instanceId });
        } catch (error) {
            this.log.error(`Failed to cancel task ${instanceId}:`, error);
            // Add error to task log
            task.logData.push({ type: 'error', data: `Failed to cancel task: ${error.message}` });
            this.updateTaskDisplay(task);
            this.updateModalDisplay(task);
        }
    }



    handleEndTask(eventData) {
        const { instanceId, remove } = eventData;

        if (remove) {
            this.removeTask(instanceId);
        } else {
            const task = this.tasks.get(instanceId);
            if (task) {
                task.status = 'completed';
                task.endTime = Date.now();
                this.updateTaskDisplay(task);
                this.updateModalDisplay(task);
                this.saveToLocalStorage();
                this.updateBadge();
                this.appClient.events.fireLocal('runtime-task-ended', { instanceId });
            }
        }

        // Close any modal associated with this task if it's being removed
        if (remove) {
            this.closeTaskModal(instanceId);
        }
    }


    addTask(instanceId, taskName, type, remoteRpcClass, showPopup = false, allowCancel = false, progressMessage = '') {
        if (this.tasks.has(instanceId)) {
            this.log.warn(`Task with instanceId ${instanceId} already exists.`);
            return;
        }

        const newTask = {
            instanceId,
            taskName,
            type,
            status: 'running',
            progress: 0,
            logData: [],
            startTime: Date.now(),
            endTime: null,
            remoteRpcClass,
            allowCancel,
            progressMessage
        };

        this.tasks.set(instanceId, newTask);
        this.saveToLocalStorage();

        // Add to UI if already rendered
        if (this.taskListElement) {
            const taskItem = this.createTaskElement(newTask);
            if (this.taskListElement.firstChild && this.taskListElement.firstChild.classList.contains('text-muted')) {
                this.taskListElement.innerHTML = ''; // Remove "No active tasks" message
            }
            this.taskListElement.appendChild(taskItem);
            this.appClient.tx.applyTranslations(taskItem);
            this.updateClearAllButtonVisibility();
            this.updateNoTasksMessageVisibility();

        }

        this.updateBadge();

        // Show modal if requested
        if (showPopup) {
            this.showTaskModal(newTask);
        }
    }


    showTaskModal(task) {
        // Create modal if it doesn't exist yet
        if (document.getElementById(`task-modal-${task.instanceId}`)) {
            // If it exists, just show it
            const modalData = this.activeModals.get(task.instanceId);
            if (modalData && modalData.instance) {
                modalData.instance.show();
            }
            return;
        }

        const modalElement = document.createElement('div');
        modalElement.classList.add('modal', 'fade', 'task-modal');
        modalElement.id = `task-modal-${task.instanceId}`;
        modalElement.setAttribute('tabindex', '-1');
        modalElement.setAttribute('aria-hidden', 'true');

        let footerButtons = `<button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Close</button>`;

        // Add cancel button for running tasks that allow cancellation
        if (task.status === 'running' && task.allowCancel) {
            footerButtons += `<button type="button" class="btn btn-danger cancel-task" data-i18n="Cancel Task">Cancel Task</button>`;
        }

        // Add remove button for completed, failed, or cancelled tasks
        if (task.status === 'completed' || task.status === 'failed' || task.status === 'cancelled') {
            footerButtons += `<button type="button" class="btn btn-outline-danger remove-task" data-i18n="Remove Task">Remove Task</button>`;
        }

        let progressMessageHtml = '';
        if (task.progressMessage) {
            progressMessageHtml = `<div class="progress-message mb-2">${task.progressMessage}</div>`;
        }


        modalElement.innerHTML = `
        <div class="modal-dialog modal-dialog-centered">
            <div class="modal-content">
                <div class="modal-header">
                    <h5 class="modal-title">${task.taskName}</h5>
                    <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                </div>
                <div class="modal-body">
                    <div class="task-status ${task.status}">${task.status}</div>
                    ${progressMessageHtml}
                    <div class="progress mt-2">
                        <div class="progress-bar" role="progressbar" style="width: ${task.progress}%" 
                            aria-valuenow="${task.progress}" aria-valuemin="0" aria-valuemax="100"></div>
                    </div>
                    <div class="log-container show mt-3"></div>
                </div>
                <div class="modal-footer">
                    ${footerButtons}
                </div>
            </div>
        </div>
    `;


        document.body.appendChild(modalElement);

        // Initialize and render logs
        const logContainer = modalElement.querySelector('.log-container');
        this.renderLogEntries(logContainer, task.logData);

        // Add event listeners for buttons
        const cancelBtn = modalElement.querySelector('.cancel-task');
        if (cancelBtn) {
            cancelBtn.addEventListener('click', () => this.cancelTask(task.instanceId));
        }

        const removeBtn = modalElement.querySelector('.remove-task');
        if (removeBtn) {
            removeBtn.addEventListener('click', () => {
                this.removeTask(task.instanceId);
                // Modal will be closed by the removeTask method
            });
        }

        // Create and store the modal instance
        const modal = new bootstrap.Modal(modalElement);
        this.activeModals.set(task.instanceId, {
            element: modalElement,
            instance: modal
        });

        // Show the modal
        modal.show();

        // Apply translations
        this.appClient.tx.applyTranslations(modalElement);

        // Set up event listener for when modal is hidden
        modalElement.addEventListener('hidden.bs.modal', () => {
            // Clean up the modal when it's hidden
            this.activeModals.delete(task.instanceId);
            // Don't remove the element yet, as this event might fire during Bootstrap's cleanup
            setTimeout(() => {
                if (document.body.contains(modalElement)) {
                    document.body.removeChild(modalElement);
                }
            }, 300);
        });
    }


    closeTaskModal(instanceId) {
        const modalData = this.activeModals.get(instanceId);
        if (modalData && modalData.instance) {
            modalData.instance.hide();
            // The 'hidden.bs.modal' event handler will clean up the rest
        }
    }

    updateProgress(instanceId, progress, logData, progressMessage = null) {
        const task = this.tasks.get(instanceId);
        if (!task) {
            this.log.warn(`Task with instanceId ${instanceId} not found for progress update.`);
            return;
        }

        task.progress = progress;

        // Update progress message if provided
        if (progressMessage !== null) {
            task.progressMessage = progressMessage;
        }

        if (logData) {
            if (typeof logData === 'string') {
                task.logData.push(logData);
            } else {
                task.logData.push(logData);
            }

            if (task.logData.length > 100) { // Limit log size
                task.logData.shift();
            }
        }

        this.saveToLocalStorage();
        this.updateTaskDisplay(task);
        this.updateModalDisplay(task);
    }

    removeTask(instanceId) {
        if (this.tasks.delete(instanceId)) {
            // Remove from UI if present
            const taskItem = this.taskListElement?.querySelector(`[data-instance-id="${instanceId}"]`);
            if (taskItem) {
                taskItem.remove();
            }
    
            this.saveToLocalStorage();
            this.updateBadge();
            this.closeTaskModal(instanceId);
    
            this.updateClearAllButtonVisibility();
            this.updateNoTasksMessageVisibility();
        }
    }
    

    clearCompletedTasks() {
        let removedAny = false;

        for (const [instanceId, task] of this.tasks.entries()) {
            if (task.status === 'completed' || task.status === 'failed' || task.status === 'cancelled') {
                this.tasks.delete(instanceId);

                // Remove from UI if present
                const taskItem = this.taskListElement?.querySelector(`[data-instance-id="${instanceId}"]`);
                if (taskItem) {
                    taskItem.remove();
                }

                this.closeTaskModal(instanceId);
                removedAny = true;
            }
        }

        if (removedAny) {
            // If no tasks left, add the "No active tasks" message
            if (this.tasks.size === 0 && this.taskListElement) {
                const emptyMessage = document.createElement('div');
                emptyMessage.classList.add('p-2', 'text-muted');
                emptyMessage.textContent = 'No active or recent tasks.';
                emptyMessage.setAttribute("data-i18n", emptyMessage.textContent);
                this.taskListElement.appendChild(emptyMessage);
                this.appClient.tx.applyTranslations(emptyMessage);
            }

            this.saveToLocalStorage();
            this.updateClearAllButtonVisibility();
            this.updateNoTasksMessageVisibility();
            this.updateBadge();
        }
    }

   handleServerEvent(eventPayload) {
        const { instanceId, type, data, code, progressMessage, taskName, allowCancel, showPopup } = eventPayload;

        if (!instanceId) {
            this.log.warn("Received server event without instanceId:", eventPayload);
            return;
        }

        // --- THIS IS THE FIX ---
        // The 'create-runtime-task' event now comes through the 'process-progress' channel
        // and needs to be handled here.
        if (type === 'create-runtime-task') {
            this.addTask(instanceId, taskName, type, null, showPopup, allowCancel, progressMessage);
            return; // Stop further processing for this event type
        }
        // --- END OF FIX ---

        const task = this.tasks.get(instanceId);
        if (!task) {
            this.log.warn(`Received event for unknown task instanceId: ${instanceId}`);
            return;
        }

        switch (type) {
            case 'progress':
                this.updateProgress(
                    instanceId,
                    data.progress,
                    data.log,
                    progressMessage || data.progressMessage
                );
                break;
            case 'cancelled':
                task.status = 'cancelled';
            case 'process-end':


                if (task.type !== 'cancelled') {
                    task.status = code === 0 ? 'completed' : 'failed';
                }
                task.endTime = Date.now();

                // Update progress message if provided
                if (progressMessage || (data && data.progressMessage)) {
                    task.progressMessage = progressMessage || data.progressMessage;
                }

                this.updateTaskDisplay(task);
                this.updateModalDisplay(task);
                this.saveToLocalStorage();
                this.updateBadge();
                this.appClient.events.fireLocal('runtime-task-ended', { instanceId });
                break;
            case 'stdout':
                task.logData.push({ type: 'stdout', data });
                if (task.logData.length > 100) { // Limit log size
                    task.logData.shift();
                }
                this.updateTaskDisplay(task);
                this.updateModalDisplay(task);
                this.saveToLocalStorage();
                break;
            case 'stderr':
                task.logData.push({ type: 'error', data });
                if (task.logData.length > 100) { // Limit log size
                    task.logData.shift();
                }
                this.updateTaskDisplay(task);
                this.updateModalDisplay(task);
                this.saveToLocalStorage();
                break;
            case 'error':
                task.status = 'failed';
                task.endTime = Date.now();

                // Update progress message if provided
                if (progressMessage || (data && data.progressMessage)) {
                    task.progressMessage = progressMessage || data.progressMessage;
                }

                task.logData.push({ type: 'error', data });
                if (task.logData.length > 100) { // Limit log size
                    task.logData.shift();
                }
                this.updateTaskDisplay(task);
                this.updateModalDisplay(task);
                this.saveToLocalStorage();
                this.updateBadge();
                this.appClient.events.fireLocal('runtime-task-ended', { instanceId });
                break;
            default:
                this.log.warn(`Unhandled server event type: ${type}`);
        }
    }

    handleLocalEvent(eventPayload) {
        // Reuse the same logic as server events
        this.handleServerEvent(eventPayload);
    }

    updateTaskDisplay(task) {
        // Find the task item in the DOM
        const taskItem = this.taskListElement?.querySelector(`[data-instance-id="${task.instanceId}"]`);
        if (!taskItem) return;

        if (task.status === 'completed' || task.status === 'failed' || task.status === 'cancelled') {
            // Recreate the task element
            const newTaskItem = this.createTaskElement(task);
            // Replace the old task item with the new one
            this.taskListElement.replaceChild(newTaskItem, taskItem);
            this.appClient.tx.applyTranslations(newTaskItem); // Apply translations to the new element
            return; // Exit to prevent further individual updates on the old element
        }

        // For running tasks, continue with the individual updates:

        // Update status (if not recreated)
        const statusElement = taskItem.querySelector('.progress-item-status');
        if (statusElement) {
            statusElement.textContent = task.status;
            statusElement.className = `progress-item-status task-status ${task.status}`; // Update status class as well
        }

        // Update progress message
        let messageElement = taskItem.querySelector('.progress-message');
        if (task.progressMessage) {
            if (!messageElement) {
                messageElement = document.createElement('div');
                messageElement.classList.add('progress-message');
                taskItem.insertBefore(messageElement, taskItem.querySelector('.progress-bar-container')); // Insert before progress bar
            }
            messageElement.textContent = task.progressMessage;
        } else if (messageElement) {
            messageElement.remove(); // Remove message element if no progressMessage
        }


        // Update progress bar
        const progressBar = taskItem.querySelector('.progress-bar');
        if (progressBar) {
            progressBar.style.width = `${task.progress}%`;
        }

        // Update log if it's visible
        const logContainer = taskItem.querySelector('.log-container');
        if (logContainer && logContainer.classList.contains('show')) {
            this.renderLogEntries(logContainer, task.logData);
            logContainer.scrollTop = logContainer.scrollHeight; // Scroll to bottom
        }

        this.updateClearAllButtonVisibility();
        this.updateNoTasksMessageVisibility();
    }

    updateModalDisplay(task) {
        const modalData = this.activeModals.get(task.instanceId);
        if (!modalData || !modalData.element) return;

        if (task.status === 'completed' || task.status === 'failed' || task.status === 'cancelled') {
            // Close the current modal
            this.closeTaskModal(task.instanceId);
            this.activeModals.delete(task.instanceId); // Remove from active modals

            // Re-show the modal which will recreate it with updated content
            this.showTaskModal(task);
            return; // Exit to prevent further individual updates on the old modal
        }


        const modalElement = modalData.element;

        // Update status (if not recreated)
        const statusElement = modalElement.querySelector('.task-status');
        if (statusElement) {
            statusElement.textContent = task.status;
            statusElement.className = `task-status ${task.status}`; // Update status class as well
        }

        // Update progress message
        let messageElement = modalElement.querySelector('.progress-message');
        if (task.progressMessage) {
            if (!messageElement) {
                // Insert after status element
                const statusDiv = modalElement.querySelector('.task-status');
                messageElement = document.createElement('div');
                messageElement.classList.add('progress-message', 'mb-2');
                statusDiv.insertAdjacentElement('afterend', messageElement);
            }
            messageElement.textContent = task.progressMessage;
        } else if (messageElement) {
            messageElement.remove(); // Remove message element if no progressMessage
        }


        // Update progress bar
        const progressBar = modalElement.querySelector('.progress-bar');
        if (progressBar) {
            progressBar.style.width = `${task.progress}%`;
            progressBar.setAttribute('aria-valuenow', task.progress);
        }

        // Update log
        const logContainer = modalElement.querySelector('.log-container');
        if (logContainer) {
            this.renderLogEntries(logContainer, task.logData);
            logContainer.scrollTop = logContainer.scrollHeight; // Scroll to bottom
        }
    }


    async refetchTaskStatus(instanceId) {
        const task = this.tasks.get(instanceId);
        if (!task || !task.remoteRpcClass || task.status === 'cancelled' || task.status === 'completed' || task.status === 'failed') return;

        try {
            const statusInfo = await this.appClient.rpcClient.call(`${task.remoteRpcClass}.getProcessStatus`, { instanceId });

            if (statusInfo) {
                task.status = statusInfo.status || task.status;
                task.startTime = statusInfo.startTime || task.startTime;
                task.endTime = statusInfo.endTime || null;

                // Update progress based on status
                if (task.status === 'completed') {
                    task.progress = 100;
                }

                // Update logs if provided
                if (statusInfo.output && Array.isArray(statusInfo.output)) {
                    // Replace existing logs with the returned output
                    task.logData = statusInfo.output;
                }

                // If process has ended, fire the appropriate event
                if (task.status !== 'running' && !task.endTime) {
                    task.endTime = Date.now();
                    this.appClient.events.fireLocal('runtime-task-ended', { instanceId });
                }

                this.updateTaskDisplay(task);
                this.updateModalDisplay(task);
                this.saveToLocalStorage();
                this.updateBadge();
            }
        } catch (error) {
            this.log.warn(`Failed to refetch task status for ${instanceId}:`, error);

            // Assume task has ended with error if we can't fetch its status
            if (task.status === 'running') {
                task.status = 'failed';
                task.endTime = Date.now();
                task.logData.push({ type: 'error', data: 'Failed to retrieve task status. The task may have been terminated.' });
                this.updateTaskDisplay(task);
                this.updateModalDisplay(task);
                this.saveToLocalStorage();
                this.updateBadge();
                this.appClient.events.fireLocal('runtime-task-ended', { instanceId });
            }
        }
    }

    loadFromLocalStorage() {
        const storedTasks = localStorage.getItem(this.localStorageKey);
        if (storedTasks) {
            try {
                const tasksObj = JSON.parse(storedTasks);
                this.tasks.clear();

                for (const [instanceId, task] of Object.entries(tasksObj)) {
                    this.tasks.set(instanceId, task);
                }
            } catch (error) {
                this.log.error("Error loading tasks from localStorage:", error);
            }
        }
    }

    saveToLocalStorage() {
        try {
            const tasksObj = {};
            for (const [instanceId, task] of this.tasks.entries()) {
                tasksObj[instanceId] = task;
            }
            localStorage.setItem(this.localStorageKey, JSON.stringify(tasksObj));
        } catch (error) {
            this.log.error("Error saving tasks to localStorage:", error);
        }
    }

    destroy() {
        this.appClient.events.server.off("process-progress", this.handleServerEvent);
        this.appClient.events.local.off("create-runtime-task", this.handleCreateTask);
        this.appClient.events.local.off("end-runtime-task", this.handleEndTask);

        // Close and clean up all modals
        for (const [instanceId, modalData] of this.activeModals.entries()) {
            if (modalData.instance) {
                modalData.instance.hide();
                if (document.body.contains(modalData.element)) {
                    document.body.removeChild(modalData.element);
                }
            }
        }

        if (this.dropdownInstance) {
            this.dropdownInstance.dispose();
            this.dropdownInstance = null;
        }
        if (this.container) this.container.remove();
        super.destroy();
    }
}